import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../../utils/tokenManager";
import axios from "axios";
import { useContactContext } from "../../../contexts/ContactContext";

/**
 * Extracted from ContactInfo.jsx (desktop) - same required-field check,
 * payload shape, and endpoint, byte-for-byte. ContactInfo.jsx now calls
 * this hook too instead of keeping its own copy, so both desktop and
 * mobile edit forms always behave identically.
 *
 * Note: `requiredFields` intentionally includes "fullName", which never
 * matches any real field key ("firstName" is the actual key) - this
 * mirrors desktop's existing behavior exactly (only lastName and email
 * are effectively enforced as required today, matching the * markers
 * shown in the UI). Not "fixed" here on purpose - this hook's job is to
 * behave identically to what's already in production, not to change it.
 */
export function useContactEditSubmit({ contact }) {
  const { refreshContacts } = useContactContext();
  const { t } = useTranslation();

  const [editMode, setEditMode] = useState(false);
  const [contactData, setContactData] = useState(contact || "");
  const [editedData, setEditedData] = useState(contact || "");
  const [isChanged, setIsChanged] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);

  const requiredFields = ["fullName", "lastName", "email"];

  const channelOptions = [
    { value: "Email", label: t("email") },
    { value: "WhatsApp", label: t("whatsapp") },
    { value: "Call", label: t("call") },
  ];

  const sectorOptions = [
    {
      value: "Agriculture, Fishing & Forestry",
      label: t("agent.contact.industry.agricultureFishingForestry"),
    },
    {
      value: "Mining & quarrying",
      label: t("agent.contact.industry.miningQuarrying"),
    },
    {
      value: "Manufacturing",
      label: t("agent.contact.industry.manufacturing"),
    },
    {
      value: "Electricity, gas & energy supply",
      label: t("agent.contact.industry.electricityGasEnergy"),
    },
    {
      value: "Water supply & waste management",
      label: t("agent.contact.industry.waterWasteManagement"),
    },
    { value: "Construction", label: t("agent.contact.industry.construction") },
    {
      value: "Wholesale & retail trade",
      label: t("agent.contact.industry.wholesaleRetailTrade"),
    },
    {
      value: "Transportation & storage",
      label: t("agent.contact.industry.transportationStorage"),
    },
    {
      value: "Accommodation & food services",
      label: t("agent.contact.industry.accommodationFoodServices"),
    },
    {
      value: "Information & communication",
      label: t("agent.contact.industry.informationCommunication"),
    },
    {
      value: "Financial & insurance activities",
      label: t("agent.contact.industry.financialInsuranceActivities"),
    },
    {
      value: "Real Estate Activities",
      label: t("agent.contact.industry.realEstateActivities"),
    },
    {
      value: "Professional, scientific & technical services",
      label: t("agent.contact.industry.professionalScientificTechnical"),
    },
    {
      value: "Administrative & support services",
      label: t("agent.contact.industry.administrativeSupportServices"),
    },
    {
      value: "Public administration & defense",
      label: t("agent.contact.industry.publicAdministrationDefense"),
    },
    { value: "Education", label: t("agent.contact.industry.education") },
    {
      value: "Human health & social work",
      label: t("agent.contact.industry.humanHealthSocialWork"),
    },
    {
      value: "Arts, entertainment & recreation",
      label: t("agent.contact.industry.artsEntertainmentRecreation"),
    },
    {
      value: "Other Service Activities",
      label: t("agent.contact.industry.otherServiceActivities"),
    },
    {
      value: "Activities of households as employers",
      label: t("agent.contact.industry.householdActivities"),
    },
    {
      value: "Extraterritorial Organizations",
      label: t("agent.contact.industry.extraterritorialOrganizations"),
    },
  ];

  useEffect(() => {
    setContactData(contact || "");
    setEditedData(contact || "");
  }, [contact]);

  const handleChange = (field, value) => {
    setEditedData((prev) => {
      const newData = { ...prev, [field]: value };
      setIsChanged(
        Object.keys(newData).some((key) => newData[key] !== contactData[key]),
      );
      setErrors((prevErrors) => ({ ...prevErrors, [field]: undefined }));
      return newData;
    });
  };

  const handleEditClick = () => {
    if (editMode) {
      setEditedData(contactData);
      setIsChanged(false);
    }
    setEditMode(!editMode);
  };

  const mapFormDataToApi = (formData) => {
    return {
      FirstName: formData.firstName,
      LastName: formData.lastName,
      Company: formData.company || "",
      Role: formData.role || "",
      Industry: formData.industry || "",
      Phone: formData.phone || "",
      Mobile: formData.cellphone || "",
      Email: formData.email,
      City: formData.city || "",
      Website: formData.website || "",
      PreferredChannel: formData.channel || "",
      ClientId: sessionStorage.getItem("clinentId"),
      Country: formData.country || "",
      InterestLevel: null,
      IsInterested: null,
      Source: null,
      Status: 1,
    };
  };

  const handleSave = async () => {
    if (!isChanged) return;

    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!editedData[field] || editedData[field].trim() === "") {
        newErrors[field] = `${t("fieldRequired", { field: t(field) })}`;
      }
    });

    try {
      setLoading(true);
      const accessToken = await getValidToken();
      const apiData = mapFormDataToApi(editedData);
      const response = await axios.put(
        `${process.env.REACT_APP_APIBASEURL}/api/contacts/${contact.contactId}`,
        apiData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (response.status === 200) {
        setContactData(editedData);
        setIsChanged(false);
        setEditMode(false);
        setSuccessModal(true);
        refreshContacts();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || t("error.generic");
      setErrorModal(true);
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
      setTimeout(() => setSuccessModal(false), 2000);
    }
  };

  const handleModal = () => {
    setErrorModal(false);
  };

  return {
    editMode,
    setEditMode,
    contactData,
    editedData,
    isChanged,
    errors,
    loading,
    successModal,
    errorModal,
    requiredFields,
    channelOptions,
    sectorOptions,
    handleChange,
    handleEditClick,
    handleSave,
    handleModal,
  };
}